import ErrorBoundary from '@/components/ErrorBoundary'
import useAuthStore from '@/stores/AuthStore'
import { useThemeContext } from '@/stores/ThemeContext'
import useConversationStore from '@/stores/ConversationStore'
import {
  App as AntApp,
  ConfigProvider,
  Empty,
  Modal, FloatButton,
  theme
} from 'antd'
import { CustomerServiceOutlined } from '@ant-design/icons'
import zhLocale from 'antd/locale/zh_CN'
import 'dayjs/locale/zh-cn'
import { useEffect, useRef } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { appendRequestHeader } from '@haina/utils-request'
import { loadPageSpy } from '@haina/utils-pagespy'

import AppLogo from '@/assets/highlights_travel_300_300.png'
import '@/assets/App.css'
import 'react-chat-elements/dist/main.css'
import EmailFetch from './Conversations/Online/Components/EmailFetch'
import FetchEmailWorker from './../workers/fetchEmailWorker?worker&url'
import { useGlobalNotify } from '@/hooks/useGlobalNotify'
import GeneratePaymentDrawer from './Conversations/Online/Components/GeneratePaymentDrawer'
import GenerateAutoDocDrawer from './Conversations/Online/Components/GenerateAutoDocDrawer'
import GenerateShorturlDrawer from './Conversations/Online/Components/GenerateShorturlDrawer'
import LogUploader from '@/components/LogUploader'
import { BUILD_VERSION } from '@/config'

const IS_MOBILE =
  navigator.userAgent.match(
    /(phone|pad|pod|iPhone|iPod|ios|iPad|Android|Mobile|BlackBerry|IEMobile|MQQBrowser|JUC|Fennec|wOSBrowser|BrowserNG|WebOS|Symbian|Windows Phone)/i,
  ) !== null

const FLOAT_BUTTON_INSET = IS_MOBILE ? 0 : 24
const FLOAT_TRIGGER = IS_MOBILE ? 'click' : null

const FONT_FAMILY =
  "-apple-system,BlinkMacSystemFont,Roboto,'Helvetica Neue',Arial,'Noto Sans',sans-serif,'Noto Color Emoji','Apple Color Emoji'"

const FLOAT_BUTTON_STYLE = {
  insetInlineEnd: FLOAT_BUTTON_INSET,
  insetBlockEnd: FLOAT_BUTTON_INSET,
  flexDirection: 'row',
}

/**
 * 全局壳：客服浮窗、日志上传与三个全局抽屉
 * 仅主应用挂载；模板预览在独立窗口运行，不需要这些能力（挂载即产生额外渲染与网络开销）
 */
function GlobalShell() {
  useGlobalNotify()

  return (
    <>
      <FloatButton.Group
        shape="square"
        placement="left"
        trigger={FLOAT_TRIGGER}
        style={FLOAT_BUTTON_STYLE}
        icon={<CustomerServiceOutlined />}
      >
        <EmailFetch />
        <LogUploader />
        <FloatButton.BackTop />
      </FloatButton.Group>
      <GeneratePaymentDrawer />
      <GenerateAutoDocDrawer />
      <GenerateShorturlDrawer />
    </>
  )
}

function startEmailWorker(userId) {
  const worker = new Worker(FetchEmailWorker, { type: 'module' })
  worker.onerror = function (error) {
    console.error('There was an error in the worker', error)
  }
  worker.onmessage = function (_event) {
    // console.log('Received message from worker', event.data, event.message);
  }
  worker.postMessage({ command: 'fetchEmail', param: { opi_sn: userId } })
  return worker
}

function AuthApp() {
  const navigate = useNavigate()

  const { colorPrimary, borderRadius } = useThemeContext()
  const loginUser = useAuthStore((state) => state.loginUser)

  const location = useLocation()
  const href = location.pathname + location.search + location.hash
  // 模板预览在独立窗口打开，只需要登录态与主题，不需要聊天/邮件/监控等全局能力
  const isStandalonePreview = location.pathname.startsWith('/ht_templates/preview')

  const connectWebsocket = useConversationStore((state) => state.connectWebsocket)
  const fetchInitialData = useConversationStore((state) => state.fetchInitialData)
  const disconnectWebsocket = useConversationStore((state) => state.disconnectWebsocket)

  const emailWorkerRef = useRef(null)

  useEffect(() => {
    // 独立窗口的模板预览不需要通知权限、监控 SDK、WebSocket 与首屏数据
    if (isStandalonePreview) return

    if (!('Notification' in window)) {
      // alert("This browser does not support desktop notification");
    } else {
      Notification.requestPermission()
    }

    if (loginUser.userId > 0) {
      appendRequestHeader('X-User-Id', loginUser.userId)
      loadPageSpy(loginUser.username + '(v' + BUILD_VERSION + ')', 'Sales CRM', true)
      connectWebsocket(loginUser.userId)
      fetchInitialData(loginUser)

      // emailWorkerRef.current = startEmailWorker(loginUser.userId)
    }

    return () => {
      disconnectWebsocket()
      const worker = emailWorkerRef.current
      if (worker) {
        worker.postMessage({ command: 'logout' })
        // 延迟终止 worker，确保 logout 消息能被处理
        const timeout = setTimeout(() => {
          worker.terminate()
        }, 300)
        // 如果组件在 300ms 内重新挂载，取消终止（worker 会被新的替换）
        return () => clearTimeout(timeout)
      }
    }
  }, [loginUser.userId, isStandalonePreview])

  // 除了路由 /p...以外都需要登陆系统
  const needToLogin = loginUser.userId === -1 && href.indexOf('/p/') === -1

  useEffect(() => {
    if (needToLogin) {
      navigate({ pathname: '/p/dingding/login', search: new URLSearchParams({ origin_url: href }).toString() })
    }
    // 销毁弹出的确认窗。处理路由前进、后退不能销毁确认对话框的问题
    Modal.destroyAll()
  }, [href])

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary,
          borderRadius,
          fontFamily: FONT_FAMILY,
        },
        algorithm: theme.defaultAlgorithm,
      }}
      locale={zhLocale}
      renderEmpty={() => <Empty description={false} />}
    >
      <AntApp>
        <ErrorBoundary>
          {!isStandalonePreview && <GlobalShell />}
          {needToLogin ? <>login...</> : <Outlet />}
          <dialog id="about-dialog" className="border-0">
            <img className="logo" src={AppLogo} alt="logo" />
            <section className="about">
              <h1>销售平台</h1>
              <h2>Sales CRM</h2>
              <p>Haina travel global sales CRM system</p>
            </section>
            <form className="actions flex gap-1" method="dialog">
              <button
                value="cancel"
                className="px-4 py-2 rounded-full border-0"
              >
                Close
              </button>
              <button
                value="install"
                id="install-button"
                className="px-4 py-2 rounded-full border-0 border-transparent bg-indigo-500 text-white"
              >
                Install app
              </button>
            </form>
          </dialog>
          <GeneratePaymentDrawer />
          <GenerateAutoDocDrawer />
          <GenerateShorturlDrawer />
        </ErrorBoundary>
      </AntApp>
    </ConfigProvider>
  )
}

export default AuthApp
